import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { getDemandAdjustments } from "../../services";
import {
  DemandAdjustment,
  DemandAdjustmentType,
} from "../../types/demand-factor.type";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { API_DATE_FORMAT } from "./constants";

/** The API's page-size ceiling; rows of one type overlapping one range stay well under it. */
const OVERLAP_QUERY_LIMIT = 100;

const OVERLAP_QUERY_DEBOUNCE_MS = 400;

const formatValidDate = (date: Dayjs | null): string =>
  date?.isValid() ? date.format(API_DATE_FORMAT) : "";

/**
 * Saved rows of the draft's type whose date range overlaps the draft's, fetched once the type
 * and both dates have settled. Target matching is left to the caller, which re-runs it on
 * every keystroke without another request.
 */
export function useOverlappingRows(
  type: DemandAdjustmentType | "",
  validFrom: Dayjs | null,
  validTo: Dayjs | null,
): DemandAdjustment[] {
  const [rows, setRows] = useState<DemandAdjustment[]>([]);

  const from = formatValidDate(validFrom);
  const to = formatValidDate(validTo);
  const isRangeOrdered = Boolean(validFrom && validTo && !validTo.isBefore(validFrom, "day"));
  const queryKey = type && from && to && isRangeOrdered ? `${type}|${from}|${to}` : "";
  const settledQueryKey = useDebounce(queryKey, OVERLAP_QUERY_DEBOUNCE_MS);
  // Rows fetched for an older type or range would warn about the wrong overlap, so nothing is
  // shown until the current query has settled and answered.
  const isSettled = queryKey !== "" && settledQueryKey === queryKey;

  useEffect(() => {
    setRows([]);
    if (!isSettled || type === "") return;

    let ignored = false;
    const loadRows = async () => {
      try {
        const {
          data: { data },
        } = await getDemandAdjustments({
          page: 1,
          limit: OVERLAP_QUERY_LIMIT,
          type,
          valid_from: from,
          valid_to: to,
        });
        if (!ignored) setRows(data?.rows ?? []);
      } catch {
        // The axios interceptor already reported the failure; the overlap warning is advisory,
        // so the form stays usable without it.
        if (!ignored) setRows([]);
      }
    };
    loadRows();

    return () => {
      ignored = true;
    };
  }, [isSettled, type, from, to]);

  return rows;
}
